<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                :title="
                    $t('partner.form.texts.title', {
                        'title-prefix': titlePrefix,
                    })
                "
            />

            <v-card-text class="pb-0">
                <div class="text-center" v-if="!resellerTypes">
                    <v-progress-circular
                        indeterminate
                        color="indigo"
                        class="ma-5"
                    ></v-progress-circular>
                </div>
                <v-row v-if="resellerTypes">
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="12"
                        class="grey lighten-4 pt-5 pb-1"
                    >
                        <label>
                            {{ $t('partner.form.labels.type-text') }}
                        </label>
                        <v-radio-group
                            v-model="reseller.reseller_type_id"
                            :error-messages="resellerTypeErrors"
                            @change="setCurrentType"
                            row
                        >
                            <v-card
                                outlined
                                class="mr-2"
                                v-for="type in resellerTypes"
                                :key="type.id"
                            >
                                <v-card-actions class="pa-5">
                                    <v-radio
                                        :label="type.name"
                                        color="blue-grey"
                                        :value="type.id"
                                        :disabled="
                                            !type.active && !allowAllTypes
                                        "
                                    ></v-radio>
                                </v-card-actions>
                            </v-card>
                        </v-radio-group>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text v-if="resellerTypes" class="pb-0">
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="reseller.trade_name"
                            :label="$t('partner.form.labels.trade-name')"
                            :error-messages="tradeNameErrors"
                            @input="$v.reseller.trade_name.$touch()"
                            @blur="$v.reseller.trade_name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="reseller.legal_name"
                            :label="$t('partner.form.labels.legal-name')"
                            :error-messages="legalNameErrors"
                            @input="$v.reseller.legal_name.$touch()"
                            @blur="$v.reseller.legal_name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="reseller.cnpj"
                            :label="$t('partner.form.labels.cnpj')"
                            v-mask="'XX.XXX.XXX/XXXX-##'"
                            :error-messages="cnpjErrors"
                            @input="handleCnpjInput"
                            @blur="$v.reseller.cnpj.$touch()"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <select-cnae
                            v-model="reseller.cnae_id"
                            :label="$t('partner.form.labels.cnae')"
                            :error-messages="cnaeErrors"
                            @input="$v.reseller.cnae_id.$touch()"
                            @blur="$v.reseller.cnae_id.$touch()"
                        ></select-cnae>
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="reseller.inscricao_estadual"
                            :label="
                                $t('partner.form.labels.inscricao-estadual')
                            "
                            :error-messages="inscricaoEstadualErrors"
                            @input="$v.reseller.inscricao_estadual.$touch()"
                            @blur="$v.reseller.inscricao_estadual.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="reseller.inscricao_municipal"
                            :label="
                                $t('partner.form.labels.inscricao-municipal')
                            "
                            :error-messages="inscricaoMunicipalErrors"
                            @input="$v.reseller.inscricao_municipal.$touch()"
                            @blur="$v.reseller.inscricao_municipal.$touch()"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="reseller.contact_name"
                            :label="$t('partner.form.labels.contact-name')"
                            :error-messages="contactNameErrors"
                            @input="$v.reseller.contact_name.$touch()"
                            @blur="$v.reseller.contact_name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="reseller.email"
                            :label="$t('partner.form.labels.email')"
                            :error-messages="emailErrors"
                            @input="$v.reseller.email.$touch()"
                            @blur="$v.reseller.email.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="reseller.phone"
                            :label="$t('partner.form.labels.phone')"
                            v-mask="['(##) ####-####', '(##) #####-####']"
                            :error-messages="phoneErrors"
                            @input="$v.reseller.phone.$touch()"
                            @blur="$v.reseller.phone.$touch()"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="isWhitelabel">
                        <v-text-field
                            v-model="reseller.website"
                            :label="$t('partner.form.labels.website')"
                            :error-messages="websiteErrors"
                            @input="$v.reseller.website.$touch()"
                            @blur="$v.reseller.website.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col v-if="isWhitelabel">
                        <v-text-field
                            v-model="reseller.cplug_domain"
                            :label="$t('partner.form.labels.cplug-domain')"
                            :error-messages="cplugDomainErrors"
                            @input="$v.reseller.cplug_domain.$touch()"
                            @blur="$v.reseller.cplug_domain.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col v-if="isWhitelabel">
                        <v-checkbox
                            v-model="reseller.self_managed"
                            :label="$t('partner.form.labels.self-managed')"
                            :value="false"
                        ></v-checkbox>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="3">
                        <v-select
                            v-model="reseller.status"
                            :items="statusOptions"
                            item-text="name"
                            item-value="value"
                            :label="$t('resources.reseller.attributes.status')"
                        ></v-select>
                    </v-col>
                    <v-col cols="3">
                        <v-text-field
                            v-model="reseller.erp_schema"
                            :label="$t('partner.form.labels.erp-schema')"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="3">
                        <v-select
                            v-model="reseller.manager_id"
                            :items="managers"
                            item-text="name"
                            item-value="id"
                            :label="
                                $t('resources.reseller.attributes.manager_id')
                            "
                        ></v-select>
                    </v-col>
                    <v-col cols="3">
                        <v-text-field
                            v-model="reseller.po"
                            :label="$t('partner.form.labels.po')"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-textarea
                            v-model="reseller.observation"
                            :label="$t('partner.form.labels.observation')"
                        ></v-textarea>
                    </v-col>
                </v-row>

                <v-row class="mb-2">
                    <v-col class="grey lighten-4 ml-2">
                        <label class="subtitle-2">
                            <v-icon left>mdi-account-cog</v-icon>Configurações
                        </label>

                        <v-row class="mt-2 mb-2">
                            <v-col>
                                <v-card outlined class="px-5 mt-5">
                                    <v-switch
                                        color="red"
                                        v-model="defaulterBlock"
                                        label="Bloqueio por inadimplência"
                                    ></v-switch>
                                </v-card>
                            </v-col>
                            <v-col>
                                <v-card outlined class="px-5 mt-5">
                                    <v-switch
                                        v-model="reseller.has_support"
                                        label="Suporte CPlug"
                                    ></v-switch>
                                </v-card>
                            </v-col>

                            <v-col>
                                <v-card outlined class="px-5 mt-5">
                                    <v-switch
                                        v-model="reseller.internal_user"
                                        label="Usuário Interno"
                                    ></v-switch>
                                </v-card>
                            </v-col>
                            <v-col v-if="isPartner">
                                <v-card outlined class="px-5 mt-5">
                                    <v-switch
                                        v-model="reseller.billing_cplug"
                                        label="Cobrança Cplug"
                                    ></v-switch>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>

                <v-col v-if="newReseller">
                    <label class="title">
                        Endereço fiscal
                    </label>

                    <address-form
                        :model="address"
                        @saved="save"
                        :titlePrefix="$t('forms.title_prefix.add')"
                        :newReseller="true"
                        ref="addressForm"
                    ></address-form>
                </v-col>
            </v-card-text>
            <form-actions :loadingSave="loading"></form-actions>
        </v-card>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import SelectCnae from '@/components/inputs/SelectCnae'
import { validationMixin } from 'vuelidate'
import {
    required,
    minLength,
    maxLength,
    email,
    requiredIf,
} from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'
import AddressModel from '@/models/Address'
import ResellerType from '@/models/ResellerType'
import AddressForm from '../addresses/Form'

export default {
    props: ['model', 'titlePrefix', 'type', 'newReseller'],

    data: () => {
        return {
            loading: false,
            resellerTypes: null,
            managers: [],
            currentType: null,
            showBlockDatePicker: false,
            defaulterBlock: false,
            address: new AddressModel(),
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
        'address-form': AddressForm,
        SelectCnae,
    },

    directives: { mask },

    mixins: [validationMixin],

    validations: {
        reseller: {
            legal_name: {
                required: requiredIf(function() {
                    return !!this.reseller.cnpj
                }),
            },
            trade_name: { required },
            cnpj: {
                required,
                maxLength: maxLength(18),
                minLength: minLength(18),
            },
            cnae_id: { required },
            inscricao_estadual: { required },
            inscricao_municipal: { required },
            contact_name: { required },
            email: { required, email },
            phone: { required, minLength: minLength(14) },
            reseller_type_id: { required },
            website: {
                required: requiredIf(function() {
                    return this.isWhitelabel
                }),
            },
            cplug_domain: {
                required: requiredIf(function() {
                    return this.isWhitelabel
                }),
            },
        },
    },

    methods: {
        async loadResellerTypes() {
            let types = await this.resellerType.all()
            this.resellerTypes = types.data

            if (this.reseller.resellerType) {
                this.setCurrentType(this.reseller.resellerType.id)
            }
        },

        async loadManagers() {
            await this.$store.dispatch('user/initManagers')
            let managers = []
            this.$store.getters['user/managers'].forEach(user =>
                managers.push({
                    name: user.name,
                    id: parseInt(user.id),
                }),
            )
            this.managers = managers
        },

        setCurrentType(type) {
            this.currentType = this.resellerTypes.find(
                mType => mType.id == type,
            )
            if (this.newReseller) {
                this.reseller.has_support = this.currentType.default_for_has_support
            }
        },

        setResellerTypes(types) {
            this.resellerTypes = types
        },

        handleDefaultBlock(model) {
            if (this.defaulterBlock) {
                model.block_reason = 'defaulting'
                model.status = 'blocked'
            }

            if (
                !this.defaulterBlock &&
                model.status == 'blocked' &&
                model.block_reason == 'defaulting'
            ) {
                model.block_reason = null
                model.status = 'active'
            }

            return model
        },

        async save() {
            this.loading = true

            this.$v.$touch()

            const isAddressInvalid =
                this.newReseller && !this.$refs.addressForm.validate()

            if (this.$v.$invalid || isAddressInvalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.reseller.clone()
            clone = this.handleDefaultBlock(clone)

            let res = null

            try {
                res = await clone.save().catch(err => {
                    err = err.response
                    if (err === 403) {
                        this.$bus.$emit('notify', 'error', err.data.message)
                    }
                })
            } finally {
                this.loading = false
            }

            if (!res) return

            if (this.newReseller) {
                this.$refs.addressForm.address.reseller_id = res.id
                this.$refs.addressForm.address.fiscal_address = true
                this.$refs.addressForm.save()
            }

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('partner.form.bus.save-success', {
                    reseller: clone.trade_name,
                }),
            )

            this.$emit('saved')
        },

        clear() {
            this.$refs.form.reset()
        },

        handleCnpjInput(value) {
            this.reseller.cnpj = value ? value.toUpperCase() : value
            this.$v.reseller.cnpj.$touch()
        },
    },

    computed: {
        reseller() {
            return this.model
        },

        resellerType() {
            return new ResellerType()
        },

        allowAllTypes() {
            return this.can('master-store resellers')
        },

        isWhitelabel() {
            if (!this.currentType) {
                return false
            }

            return this.currentType.referable == 'whitelabel'
        },

        isPartner() {
            if (!this.currentType) {
                return false
            }

            return this.currentType.referable == 'partner'
        },

        legalNameErrors() {
            const errors = []
            if (!this.$v.reseller.legal_name.$dirty) return errors

            !this.$v.reseller.legal_name.required &&
                errors.push(
                    this.$t('partner.form.messages.legalName-error-required'),
                )
            return errors
        },

        tradeNameErrors() {
            const errors = []
            if (!this.$v.reseller.trade_name.$dirty) return errors

            !this.$v.reseller.trade_name.required &&
                errors.push(
                    this.$t('partner.form.messages.tradeName-error-required'),
                )
            return errors
        },

        cnpjErrors() {
            const errors = []
            if (!this.$v.reseller.cnpj.$dirty) return errors

            !this.$v.reseller.cnpj.required &&
                errors.push(
                    this.$t('partner.form.messages.cnpj-error-required'),
                )

            !this.$v.reseller.cnpj.maxLength &&
                errors.push(this.$t('partner.form.messages.cnpj-error-length'))

            !this.$v.reseller.cnpj.minLength &&
                errors.push(this.$t('partner.form.messages.cnpj-error-length'))

            return errors
        },

        cnaeErrors() {
            const errors = []
            if (!this.$v.reseller.cnae_id.$dirty) return errors

            !this.$v.reseller.cnae_id.required &&
                errors.push(
                    this.$t('partner.form.messages.cnae-error-required'),
                )
            return errors
        },

        inscricaoEstadualErrors() {
            const errors = []
            if (!this.$v.reseller.inscricao_estadual.$dirty) return errors

            !this.$v.reseller.inscricao_estadual.required &&
                errors.push(
                    this.$t(
                        'partner.form.messages.inscricao-estadual-error-required',
                    ),
                )
            return errors
        },

        inscricaoMunicipalErrors() {
            const errors = []
            if (!this.$v.reseller.inscricao_municipal.$dirty) return errors

            !this.$v.reseller.inscricao_municipal.required &&
                errors.push(
                    this.$t(
                        'partner.form.messages.inscricao-municipal-error-required',
                    ),
                )
            return errors
        },

        websiteErrors() {
            const errors = []
            if (!this.$v.reseller.website.$dirty) return errors

            !this.$v.reseller.website.required &&
                errors.push(
                    this.$t('partner.form.messages.website-error-required'),
                )
            return errors
        },

        cplugDomainErrors() {
            const errors = []
            if (!this.$v.reseller.cplug_domain.$dirty) return errors

            !this.$v.reseller.cplug_domain.required &&
                errors.push(
                    this.$t('partner.form.messages.cplugDomain-error-required'),
                )
            return errors
        },

        resellerTypeErrors() {
            const errors = []

            if (!this.$v.reseller.reseller_type_id.$dirty) return errors

            !this.$v.reseller.reseller_type_id.required &&
                errors.push(
                    this.$t('partner.form.messages.typeText-error-required'),
                )
            return errors
        },

        contactNameErrors() {
            const errors = []
            if (!this.$v.reseller.contact_name.$dirty) return errors

            !this.$v.reseller.contact_name.required &&
                errors.push(
                    this.$t('partner.form.messages.contactName-error-required'),
                )
            return errors
        },

        emailErrors() {
            const errors = []
            if (!this.$v.reseller.email.$dirty) return errors

            !this.$v.reseller.email.required &&
                errors.push(
                    this.$t('partner.form.messages.email-error-required'),
                )

            !this.$v.reseller.email.email &&
                errors.push(this.$t('partner.form.messages.email-error-email'))

            return errors
        },

        phoneErrors() {
            const errors = []
            if (!this.$v.reseller.phone.$dirty) return errors

            !this.$v.reseller.phone.required &&
                errors.push(
                    this.$t('partner.form.messages.phone-error-required'),
                )

            !this.$v.reseller.phone.minLength &&
                errors.push(
                    this.$t('partner.form.messages.phone-error-min-length'),
                )

            return errors
        },

        showFormattedDate() {
            if (!this.model.block_at) return null

            const [year, month, day] = this.model.block_at.split('-')
            return `${day}/${month}/${year}`
        },

        statusOptions() {
            let unchangeable = ['new', 'pendent', 'deleted']
            return [
                {
                    name: this.$t('resources.reseller.getters.status.new'),
                    value: 'new',
                    disabled: this.model.id,
                },
                {
                    name: this.$t('resources.reseller.getters.status.pendent'),
                    value: 'pendent',
                    disabled: true,
                },
                {
                    name: this.$t('resources.reseller.getters.status.active'),
                    value: 'active',
                    disabled:
                        !this.model.id ||
                        unchangeable.includes(this.model.status),
                },
                {
                    name: this.$t('resources.reseller.getters.status.blocked'),
                    value: 'blocked',
                    disabled:
                        !this.model.id ||
                        unchangeable.includes(this.model.status),
                },
                {
                    name: this.$t('resources.reseller.getters.status.canceled'),
                    value: 'canceled',
                    disabled:
                        !this.model.id ||
                        unchangeable.includes(this.model.status),
                },
                {
                    name: this.$t('resources.reseller.getters.status.deleted'),
                    value: 'deleted',
                    disabled: true,
                },
            ]
        },
    },

    mounted() {
        if (this.type == 'create') this.reseller.active = true
    },

    created() {
        this.defaulterBlock =
            this.model.status == 'blocked' &&
            this.model.block_reason == 'defaulting'
        this.loadResellerTypes()
        this.loadManagers()
    },
}
</script>
